import { useEffect, useRef, useState } from 'react'

import { Check, ChevronDown, Layers } from 'lucide-react'

interface GroupSelectProps<T extends string> {
  options: { value: T; label: string }[]
  value: T
  /** The "no grouping" sentinel — the trigger drops its tint and reads plain "Group" at this value. */
  noneValue: T
  onChange: (value: T) => void
}

/**
 * The toolbars' "Group" control: one dropdown choosing the list's grouping dimension. Generic over
 * the dimension union so the Issues and Coverage boards share one control. Grouping is
 * presentation-only — it never changes which rows are in the working set.
 */
export function GroupSelect<T extends string>({ options, value, noneValue, onChange }: GroupSelectProps<T>) {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const grouped = value !== noneValue
  const label = options.find((o) => o.value === value)?.label ?? 'None'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-[30px] items-center gap-[6px] rounded-[9px] border px-[10px] text-[12px] font-semibold transition-colors ${
          grouped || open
            ? 'border-[#c7d2ea] bg-[#eef2fb] text-gis-navy'
            : 'border-gis-line bg-white text-gis-ink-2 hover:border-[#d6dbe6]'
        }`}
      >
        <Layers className="h-[13px] w-[13px]" aria-hidden />
        {grouped ? `Group: ${label}` : 'Group'}
        <ChevronDown className="h-[13px] w-[13px] text-gis-muted" aria-hidden />
      </button>

      {open && (
        <div className="absolute top-[34px] right-0 z-40 w-[172px] rounded-[12px] border border-slate-900/10 bg-white p-1 shadow-xl">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value)
                setOpen(false)
              }}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-slate-100 ${
                o.value === value ? 'font-bold text-gis-navy' : 'text-gis-ink-2'
              }`}
            >
              {o.label}
              {o.value === value && <Check className="h-[13px] w-[13px]" aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
